import {
  CREDIT_NOTE_REASONS,
  PAYMENT_METHODS,
  REFUND_METHODS,
  REFUND_REASONS,
  SERVICE_ITEM_CATEGORIES,
  WAIVER_REASONS,
} from '@wodi/domain';
import { z } from 'zod';

import { fiscalStatusSchema, receiptFiscalSchema } from './etims.ts';
import { amountSchema, centsStringSchema, signedAmountSchema } from './money.ts';

// Charges, the visit bill, payments, receipts and cashier shifts
// (docs/plans/phase1-billing.md §3). Money is integer cents in a string (catalogue decision
// 19). Bills are addressed by visit UUID; no patient data travels in a URL or query string.

export const CHARGE_SOURCE_TYPES = [
  'queue_entry',
  'manual',
  'lab_order',
  'imaging_order',
  'procedure',
  'dispense_line',
  'sale_line',
  'bed_day',
  'package',
  // Theatre lite (docs/plans/phase2-theatre.md §2.7).
  'theatre_case',
  'theatre_charge',
  // A dose given from ward stock (docs/plans/phase2-emar-inpatient-pharmacy.md §4.4).
  'medication_administration',
  // A day of a body in the mortuary (docs/plans/phase2-mortuary-and-notifications.md §4).
  'mortuary_storage_day',
  // A vaccine dose given from the immunisation store (docs/plans/phase2-maternity-mch.md §2.1).
  'immunisation_dose',
] as const;

export const BILL_LINE_STATES = [
  'cancelled',
  'not_priced',
  'nothing_to_pay',
  'owed',
  'part_paid',
  'paid',
] as const;

const reasonSchema = z.string().trim().min(3, 'Say why, in a few words').max(500);

export const chargeViewSchema = z.object({
  id: z.uuid(),
  sourceType: z.enum(CHARGE_SOURCE_TYPES),
  serviceItemId: z.uuid(),
  itemCode: z.string(),
  itemName: z.string(),
  category: z.enum(SERVICE_ITEM_CATEGORIES),
  billingUnit: z.string(),
  quantity: z.number().int(),
  occurredAt: z.iso.datetime(),
  status: z.enum(['pending', 'billed', 'cancelled']),
  state: z.enum(BILL_LINE_STATES),
  /** A `price.*` or `split.*` code, and its words, while the charge has no price. */
  unpricedReason: z.string().nullable(),
  unpricedMessage: z.string().nullable(),
  unitPrice: amountSchema.nullable(),
  total: amountSchema,
  payerAmount: amountSchema,
  patientAmount: amountSchema,
  adjusted: amountSchema,
  credited: amountSchema,
  due: amountSchema,
  paid: amountSchema,
  owed: amountSchema,
  manualReason: z.string().nullable(),
  cancelReason: z.string().nullable(),
  /** Whether the patient's share is paid, waived or free: what a pay-first gate asks. */
  covered: z.boolean(),
  /** The invoice line for the patient's share, once billed: what a credit note credits. */
  invoiceLineId: z.uuid().nullable(),
  invoiceId: z.uuid().nullable(),
  /** What can still be credited on that line. */
  creditable: amountSchema,
});

export type ChargeView = z.infer<typeof chargeViewSchema>;

export const billPaymentSchema = z.object({
  id: z.uuid(),
  method: z.enum(PAYMENT_METHODS),
  amount: amountSchema,
  tendered: amountSchema.nullable(),
  change: amountSchema.nullable(),
  reference: z.string().nullable(),
  receivedAt: z.iso.datetime(),
  cashier: z.object({ id: z.uuid(), name: z.string() }),
  receipt: z.object({ id: z.uuid(), number: z.string() }).nullable(),
  /** What can still be refunded from this payment now. */
  refundable: amountSchema,
});

export const billInvoiceSchema = z.object({
  id: z.uuid(),
  number: z.string(),
  issuedAt: z.iso.datetime(),
  total: amountSchema,
  fiscalStatus: fiscalStatusSchema,
});

export const billTotalsSchema = z.object({
  charged: amountSchema,
  payerAmount: amountSchema,
  patientAmount: amountSchema,
  adjusted: amountSchema,
  credited: amountSchema,
  due: amountSchema,
  paid: amountSchema,
  owed: amountSchema,
  patientCredit: amountSchema,
  unpricedCount: z.number().int().min(0),
});

// --- Waivers and discounts ---------------------------------------------------------------------

export const adjustmentBodySchema = z
  .object({
    kind: z.enum(['waiver', 'discount']),
    basis: z.enum(['percent', 'amount']),
    rateBasisPoints: z.number().int().min(1).max(10_000).optional(),
    amountCents: centsStringSchema.optional(),
    reasonCode: z.enum(WAIVER_REASONS),
    reasonNote: z.string().trim().max(500).optional(),
  })
  .refine(
    (b) => (b.basis === 'percent' ? b.rateBasisPoints !== undefined : b.amountCents !== undefined),
    {
      message: 'Give a percentage or an amount',
    },
  )
  .refine((b) => b.reasonCode !== 'other' || (b.reasonNote ?? '').length > 0, {
    message: 'Say why when the reason is other',
    path: ['reasonNote'],
  });

export const adjustmentDecisionBodySchema = z.object({
  note: z.string().trim().max(500).optional(),
});

export const adjustmentViewSchema = z.object({
  id: z.uuid(),
  chargeId: z.uuid(),
  visitId: z.uuid(),
  kind: z.enum(['waiver', 'discount']),
  basis: z.enum(['percent', 'amount']),
  rateBasisPoints: z.number().int().nullable(),
  amount: amountSchema,
  reasonCode: z.string(),
  reasonNote: z.string().nullable(),
  status: z.enum(['requested', 'approved', 'rejected', 'withdrawn']),
  requestedBy: z.object({ id: z.uuid(), name: z.string() }),
  requestedAt: z.iso.datetime(),
  decidedBy: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  decidedAt: z.iso.datetime().nullable(),
  decisionNote: z.string().nullable(),
  canDecide: z.boolean(),
});

export type AdjustmentRequestView = z.infer<typeof adjustmentViewSchema>;

// --- Credit notes and refunds -------------------------------------------------------------------

const personRefSchema = z.object({ id: z.uuid(), name: z.string() });

export const creditNoteBodySchema = z
  .object({
    lines: z
      .array(z.object({ invoiceLineId: z.uuid(), amountCents: centsStringSchema }))
      .min(1)
      .max(100),
    reasonCode: z.enum(CREDIT_NOTE_REASONS),
    reasonNote: z.string().trim().max(500).optional(),
  })
  .refine((b) => b.reasonCode !== 'other' || (b.reasonNote ?? '').length > 0, {
    message: 'Say why when the reason is other',
    path: ['reasonNote'],
  });

export const creditNoteViewSchema = z.object({
  id: z.uuid(),
  visitId: z.uuid(),
  invoiceId: z.uuid(),
  invoiceNumber: z.string(),
  number: z.string().nullable(),
  total: amountSchema,
  lines: z.array(
    z.object({
      invoiceLineId: z.uuid(),
      chargeId: z.uuid(),
      description: z.string(),
      amount: amountSchema,
    }),
  ),
  reasonCode: z.string(),
  reasonNote: z.string().nullable(),
  status: z.enum(['requested', 'approved', 'rejected', 'withdrawn']),
  requestedBy: personRefSchema,
  requestedAt: z.iso.datetime(),
  decidedBy: personRefSchema.nullable(),
  decidedAt: z.iso.datetime().nullable(),
  decisionNote: z.string().nullable(),
  canDecide: z.boolean(),
});

export type CreditNoteView = z.infer<typeof creditNoteViewSchema>;

export const refundBodySchema = z
  .object({
    amountCents: centsStringSchema,
    method: z.enum(REFUND_METHODS),
    reasonCode: z.enum(REFUND_REASONS),
    reasonNote: z.string().trim().max(500).optional(),
  })
  .refine((b) => b.reasonCode !== 'other' || (b.reasonNote ?? '').length > 0, {
    message: 'Say why when the reason is other',
    path: ['reasonNote'],
  });

export const payRefundBodySchema = z.object({
  /** Required for an M-Pesa or bank reversal: the reference of the reversal made there. */
  externalReference: z.string().trim().min(3).max(40).optional(),
});

export const refundViewSchema = z.object({
  id: z.uuid(),
  visitId: z.uuid(),
  paymentId: z.uuid(),
  number: z.string(),
  method: z.enum(REFUND_METHODS),
  amount: amountSchema,
  reasonCode: z.string(),
  reasonNote: z.string().nullable(),
  status: z.enum(['requested', 'approved', 'rejected', 'withdrawn', 'paid']),
  requestedBy: personRefSchema,
  requestedAt: z.iso.datetime(),
  decidedBy: personRefSchema.nullable(),
  decidedAt: z.iso.datetime().nullable(),
  decisionNote: z.string().nullable(),
  paidBy: personRefSchema.nullable(),
  paidAt: z.iso.datetime().nullable(),
  externalReference: z.string().nullable(),
  canDecide: z.boolean(),
});

export type RefundView = z.infer<typeof refundViewSchema>;

export const billViewSchema = z.object({
  visit: z.object({
    id: z.uuid(),
    visitNumber: z.string(),
    status: z.enum(['open', 'closed', 'cancelled']),
    /** Null for a counter sale to a buyer who wasn't identified (ADR 0014). */
    patientId: z.uuid().nullable(),
    startedAt: z.iso.datetime(),
    /**
     * A masked bill: the visit carries a sensitivity label the reader's roles aren't granted.
     * It stays payable; the patient shows only by number and each item as "Restricted item".
     */
    restricted: z.boolean().optional(),
    /** Set on a masked bill, which has no patient ID to load a banner from. */
    patientNumber: z.string().nullable().optional(),
  }),
  payer: z.object({ id: z.uuid().nullable(), kind: z.string(), name: z.string() }),
  charges: z.array(chargeViewSchema),
  /** Waivers and discounts asked for on this visit, newest first, with their decisions. */
  adjustments: z.array(adjustmentViewSchema),
  creditNotes: z.array(creditNoteViewSchema),
  refunds: z.array(refundViewSchema),
  invoices: z.array(billInvoiceSchema),
  payments: z.array(billPaymentSchema),
  totals: billTotalsSchema,
});

export type BillView = z.infer<typeof billViewSchema>;

export const billListQuerySchema = z.object({
  state: z.enum(['owing', 'all']).default('owing'),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.uuid().optional(),
});

export const billListItemSchema = z.object({
  visitId: z.uuid(),
  visitNumber: z.string(),
  visitStatus: z.enum(['open', 'closed', 'cancelled']),
  startedAt: z.iso.datetime(),
  patient: z.object({ id: z.uuid(), name: z.string(), patientNumber: z.string() }),
  owed: amountSchema,
  unpricedCount: z.number().int().min(0),
});

export type BillListItem = z.infer<typeof billListItemSchema>;

export const billListSchema = z.object({
  items: z.array(billListItemSchema),
  nextCursor: z.uuid().nullable(),
});

// --- Charges ---------------------------------------------------------------------------------

export const manualChargeBodySchema = z.object({
  clientRequestId: z.uuid(),
  serviceItemId: z.uuid(),
  quantity: z.number().int().min(1).max(10_000),
  /** When the service was given; defaults to now. Prices at this instant. */
  occurredAt: z.iso.datetime().optional(),
  reason: reasonSchema,
});

export const cancelChargeBodySchema = z.object({ reason: reasonSchema });

export const priceAgainResultSchema = z.object({
  priced: z.number().int().min(0),
  stillUnpriced: z.number().int().min(0),
});

export const unpricedChargeSchema = z.object({
  chargeId: z.uuid(),
  visitId: z.uuid(),
  visitNumber: z.string(),
  serviceItemId: z.uuid(),
  itemCode: z.string(),
  itemName: z.string(),
  occurredAt: z.iso.datetime(),
  reason: z.string(),
  message: z.string(),
});

export const unpricedChargeListSchema = z.object({ items: z.array(unpricedChargeSchema) });

/** One open request on the approvals list, with what an approver needs to decide it. */
export const billingApprovalSchema = z.object({
  adjustment: adjustmentViewSchema,
  visitNumber: z.string(),
  patient: z.object({ id: z.uuid(), name: z.string(), patientNumber: z.string() }),
  itemName: z.string(),
  patientShare: amountSchema,
});

export type BillingApprovalView = z.infer<typeof billingApprovalSchema>;

export const billingApprovalListSchema = z.object({
  items: z.array(billingApprovalSchema),
  creditNotes: z.array(
    z.object({
      creditNote: creditNoteViewSchema,
      visitNumber: z.string(),
      patient: z.object({ id: z.uuid(), name: z.string(), patientNumber: z.string() }),
    }),
  ),
  refunds: z.array(
    z.object({
      refund: refundViewSchema,
      visitNumber: z.string(),
      patient: z.object({ id: z.uuid(), name: z.string(), patientNumber: z.string() }),
    }),
  ),
});

// --- Payments ----------------------------------------------------------------------------------

export const tenderSchema = z.discriminatedUnion('method', [
  z.object({
    method: z.literal('cash'),
    tenderedCents: centsStringSchema,
    /** What this cash pays; defaults to the lesser of what is owed and what was handed over. */
    amountCents: centsStringSchema.optional(),
  }),
  z.object({
    method: z.enum(['mpesa', 'card', 'bank_transfer']),
    amountCents: centsStringSchema,
    /** The M-Pesa transaction code, card slip or bank reference. */
    reference: z.string().trim().min(3).max(40),
  }),
]);

export type Tender = z.infer<typeof tenderSchema>;

export const takePaymentBodySchema = z.object({
  clientRequestId: z.uuid(),
  /** The charges being paid for, in the order to pay them. Default: everything owed, oldest first. */
  chargeIds: z.array(z.uuid()).min(1).max(200).optional(),
  tenders: z.array(tenderSchema).min(1).max(4),
  /** Only for a paper receipt entered later the same day (plan §13 decision 14). */
  receivedAt: z.iso.datetime().optional(),
  manualReceiptReference: z.string().trim().min(1).max(40).optional(),
  receivedAtReason: reasonSchema.optional(),
});

export const takePaymentResultSchema = z.object({
  payments: z.array(
    z.object({
      paymentId: z.uuid(),
      receiptId: z.uuid(),
      receiptNumber: z.string(),
      method: z.enum(PAYMENT_METHODS),
      amount: amountSchema,
      change: amountSchema.nullable(),
    }),
  ),
  invoice: z.object({ id: z.uuid(), number: z.string() }).nullable(),
  bill: billViewSchema,
});

export const issueBillResultSchema = z.object({
  invoice: z.object({ id: z.uuid(), number: z.string() }).nullable(),
  bill: billViewSchema,
});

// --- Receipts ----------------------------------------------------------------------------------

export const receiptViewSchema = z.object({
  id: z.uuid(),
  number: z.string(),
  issuedAt: z.iso.datetime(),
  /** 0 before the first print; a print when this is above 0 is marked "Copy". */
  printCount: z.number().int().min(0),
  facility: z.object({ name: z.string(), mflCode: z.string().nullable() }),
  /** Null for a counter sale to a buyer who wasn't identified (ADR 0014). */
  patient: z.object({ name: z.string(), patientNumber: z.string() }).nullable(),
  visitNumber: z.string(),
  cashier: z.string(),
  payment: z.object({
    method: z.enum(PAYMENT_METHODS),
    amount: amountSchema,
    tendered: amountSchema.nullable(),
    change: amountSchema.nullable(),
    reference: z.string().nullable(),
    receivedAt: z.iso.datetime(),
    manualReceiptReference: z.string().nullable(),
  }),
  lines: z.array(
    z.object({
      description: z.string(),
      quantity: z.number().int(),
      invoiceNumber: z.string(),
      amount: amountSchema,
    }),
  ),
  /** What the patient still owes on the visit after this payment. */
  balanceOwed: amountSchema,
  /** A deposit held against an inpatient stay prints as a deposit receipt (inpatient billing plan). */
  purpose: z.enum(['payment', 'deposit']).optional(),
  /** The first invoice's state; `fiscal` has each one. Nothing KRA didn't return is printed. */
  fiscalStatus: fiscalStatusSchema,
  /** eTIMS signing per invoice the payment paid (docs/plans/phase3-etims-vscu.md §5). */
  fiscal: z.array(receiptFiscalSchema),
});

export type ReceiptView = z.infer<typeof receiptViewSchema>;

export const receiptPrintedSchema = z.object({ copyNumber: z.number().int().min(1) });

// --- Cashier shifts ------------------------------------------------------------------------------

const byMethodSchema = z.object({
  cash: amountSchema,
  mpesa: amountSchema,
  card: amountSchema,
  bank_transfer: amountSchema,
});

export const openShiftBodySchema = z.object({
  clientRequestId: z.uuid(),
  openingFloatCents: centsStringSchema,
});

export const closeShiftBodySchema = z.object({
  countedCashCents: centsStringSchema,
  varianceReason: z.string().trim().max(500).optional(),
  /** Required when a supervisor closes someone else's shift. */
  closeReason: z.string().trim().max(500).optional(),
});

export const shiftViewSchema = z.object({
  id: z.uuid(),
  number: z.string(),
  status: z.enum(['open', 'closed']),
  cashier: z.object({ id: z.uuid(), name: z.string() }),
  openedAt: z.iso.datetime(),
  openingFloat: amountSchema,
  received: byMethodSchema,
  refunded: byMethodSchema,
  expectedCash: amountSchema,
  paymentCount: z.number().int().min(0),
  closedAt: z.iso.datetime().nullable(),
  closedBy: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  countedCash: amountSchema.nullable(),
  variance: signedAmountSchema.nullable(),
  varianceReason: z.string().nullable(),
  closeReason: z.string().nullable(),
});

export type ShiftView = z.infer<typeof shiftViewSchema>;

export const currentShiftSchema = z.object({ shift: shiftViewSchema.nullable() });

export const shiftListQuerySchema = z.object({
  status: z.enum(['open', 'closed']).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.uuid().optional(),
});

export const shiftListSchema = z.object({
  items: z.array(shiftViewSchema),
  nextCursor: z.uuid().nullable(),
});

// --- Settings ----------------------------------------------------------------------------------

export const billingSettingsSchema = z.object({
  payBefore: z.array(z.enum(SERVICE_ITEM_CATEGORIES)),
  version: z.number().int(),
  /** Consultation service points here, and the fee each charges at check-in. */
  consultationPoints: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      status: z.enum(['active', 'inactive']),
      chargeItem: z.object({ id: z.uuid(), code: z.string(), name: z.string() }).nullable(),
    }),
  ),
});

export const updateBillingSettingsBodySchema = z.object({
  payBefore: z.array(z.enum(SERVICE_ITEM_CATEGORIES)).max(SERVICE_ITEM_CATEGORIES.length),
  expectedVersion: z.number().int().min(0),
});

export const servicePointChargeItemBodySchema = z.object({
  serviceItemId: z.uuid().nullable(),
});
